import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { preserveMode } from "@/hooks/useProjectMode";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BookOpen,
  Briefcase,
  Bug,
  Calendar,
  CalendarDays,
  CheckSquare,
  Clock,
  Command,
  Contact,
  FileText,
  Filter,
  FlaskConical,
  FolderKanban,
  GitBranch,
  GitCompare,
  HardDrive,
  History,
  Layers,
  LayoutDashboard,
  LayoutTemplate,
  ListChecks,
  Loader2,
  Mail,
  Milestone,
  PenLine,
  Radio,
  ScrollText,
  Search,
  Server,
  Settings,
  Share2,
  Shield,
  Target,
  Users,
  Video,
  Wand2,
  Rocket,
} from "lucide-react";
import { Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useV3Flag, V3_FLAGS } from "@/lib/featureFlagsV3";
import { isHiddenSurface } from "@/lib/projectSurfaces";
import { searchApi, gmailApi, calendarApi, driveApi } from "@/api/services";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";
import { useTemplateLabels, type LabelResolver } from "@/hooks/useTemplateLabels";
import { plainStatus } from "@/lib/projectFacts";
import { usePresenting } from "@/stores/presenting";
import { withoutProjectCopies } from "@/lib/presentingFilters";
import { plainTaskTitle } from "@/lib/plainTitle";
import type { UnifiedSearchResult } from "@/api/types";

// ---- Types ----

interface PaletteItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  section: string;
  keywords: string[];
}

interface EntityResult {
  id: string;
  title: string;
  type: string;
  path: string;
  status?: string;
  subtitle?: string;
  similarity?: number;
  matchSource?: "semantic" | "keyword" | "both";
}

type EntityType =
  | "Project"
  | "Objective"
  | "Task"
  | "Test Case"
  | "Defect"
  | "Document"
  | "Component"
  | "User Story"
  | "Module"
  | "Specification";

// ---- Status badge colors ----

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  ACTIVE: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  IN_PROGRESS: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  APPROVED: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  COMPLETED: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  DONE: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  PASSED: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  BLOCKED: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  OPEN: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  REVIEW: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  IN_REVIEW: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  CLOSED: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  DEPRECATED: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
};

function getStatusColor(status?: string): string {
  if (!status) return "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400";
  return STATUS_COLORS[status.toUpperCase()] ?? "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400";
}

// ---- Entity type icons & colors ----

const ENTITY_TYPE_META: Record<EntityType, { icon: LucideIcon; color: string }> = {
  Project: { icon: FolderKanban, color: "text-blue-600 dark:text-blue-400" },
  Objective: { icon: Target, color: "text-orange-600 dark:text-orange-400" },
  Task: { icon: CheckSquare, color: "text-indigo-600 dark:text-indigo-400" },
  "Test Case": { icon: FlaskConical, color: "text-purple-600 dark:text-purple-400" },
  Defect: { icon: Bug, color: "text-red-600 dark:text-red-400" },
  Document: { icon: FileText, color: "text-cyan-600 dark:text-cyan-400" },
  Component: { icon: Layers, color: "text-teal-600 dark:text-teal-400" },
  "User Story": { icon: BookOpen, color: "text-pink-600 dark:text-pink-400" },
  Module: { icon: Layers, color: "text-emerald-600 dark:text-emerald-400" },
  Specification: { icon: FileText, color: "text-sky-600 dark:text-sky-400" },
};

// ---- Recent items (localStorage) ----

const RECENT_KEY = "ftl_command_palette_recent";
const SEARCH_HISTORY_KEY = "ftl_command_palette_searches";
const MAX_RECENT = 5;
const MAX_SEARCH_HISTORY = 8;

function getRecentItems(): PaletteItem[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentItem(item: PaletteItem) {
  try {
    const recent = getRecentItems().filter((r) => r.id !== item.id);
    recent.unshift(item);
    localStorage.setItem(
      RECENT_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT)),
    );
  } catch {
    /* storage full */
  }
}

function getSearchHistory(): string[] {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveSearchQuery(query: string) {
  try {
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    const history = getSearchHistory().filter((q) => q !== trimmed);
    history.unshift(trimmed);
    localStorage.setItem(
      SEARCH_HISTORY_KEY,
      JSON.stringify(history.slice(0, MAX_SEARCH_HISTORY)),
    );
  } catch {
    /* storage full */
  }
}

function clearSearchHistory() {
  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch {
    /* ignore */
  }
}

// ---- Text highlighting ----

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query.trim() || query.length < 2) {
    return <>{text}</>;
  }
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 text-inherit rounded-sm px-0.5">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

// ---- Navigation items ----

// `skin` is the project's label resolver (from useTemplateLabels); when no
// project is active (empty projectBase) it is the identity fallback, so the
// global command palette keeps its canonical labels. Only the PROJECT-scoped
// entity-type rows are skinned; each passes its current word as the fallback so
// a non-template project is byte-identical.
export function buildItems(
  projectBase: string,
  skin: LabelResolver,
  iteratorEnabled: boolean,
  // Row 4aa3e0ec: route slugs this project type does not use. A finance
  // project's palette offers no Iterator, Operate, Ship or Broadcast.
  hiddenSurfaces: readonly string[] = [],
): PaletteItem[] {
  const inProject = projectBase.length > 0;
  const entLabel = (canonical: string, fallback: string): string =>
    inProject ? skin(canonical, { plural: true, fallback }) : fallback;
  return [
    // "Dashboard" → "Home" (nav-consolidation PR-1): one home, one word —
    // matches the FelixRail primary label. The id stays "dashboard" so
    // previously saved recent-items entries keep resolving.
    { id: "dashboard", label: "Home", path: "/", icon: LayoutDashboard, section: "Main", keywords: ["home", "dashboard", "overview", "kpi", "metrics"] },
    { id: "projects", label: "Projects", path: "/projects", icon: FolderKanban, section: "Main", keywords: ["project", "manage", "portfolio"] },
    // Primary-rail vocabulary (nav-consolidation PR-1): the palette now names
    // the same flagship destinations as the FelixRail primaries.
    { id: "portfolio", label: "Portfolio", path: "/portfolio", icon: Briefcase, section: "Main", keywords: ["portfolio", "program", "hub", "constellation"] },
    { id: "communications", label: "Communications", path: "/calendar", icon: Calendar, section: "Main", keywords: ["communications", "calendar", "schedule", "meetings", "action items"] },
    // Flagship project-mode surfaces. Their routes exist ONLY under
    // /projects/:projectId (verified in App.tsx — no org-level equivalent),
    // so they render only when the palette has project context, via the same
    // useParams-driven `projectBase` mechanism the entity rows use. They are
    // workflow/mode labels, not canonical entity types, so no skinLabel.
    ...(inProject
      ? [
          // Gated for the same reason as the rail: pages/Iterator.tsx refuses
          // with a "not enabled" panel unless ftl.ux.v3.iterator_mvp is on, and
          // this palette is the SECOND mounted door to it. Fixing only the rail
          // would leave the command palette walking people into the refusal.
          //
          // Kept as a plain literal and filtered at the end of this function
          // rather than spread conditionally: a `: []` between the `inProject`
          // guard and the hosting entry is what test_ship_can_be_found.py reads
          // as the end of the project-scoped block, and a conditional spread
          // here made that guard report hosting as globally scoped.
          { id: "iterator", label: "Iterator", path: `${projectBase}/iterator`, icon: Wand2, section: "Main", keywords: ["iterator", "build", "canvas", "iterate", "ai builder"] },
          { id: "operate", label: "Operate", path: `${projectBase}/operate`, icon: Server, section: "Main", keywords: ["operate", "operations", "deployment", "monitor", "runtime"] },
          // SHIP (row fc91aab1). /projects/:projectId/hosting was reachable by
          // TYPING THE URL and nothing else: absent from navData AND from this
          // palette, while its three sibling flagship surfaces are in one or
          // both. Measured 2026-09-05 -- operate 0/1, iterator 1/1, develop 1/0,
          // hosting 0/0. The feature the product exists for was the only one
          // nobody could find.
          //
          // Safe to surface: ftl.ux.v3.tenant_hosting is ENABLED for all 20 orgs
          // carrying flag rows, so this opens a live page rather than a "feature
          // disabled" notice. Checking that is the difference between fixing
          // discoverability and advertising a dark feature.
          { id: "hosting", label: "Ship", path: `${projectBase}/hosting`, icon: Rocket, section: "Main", keywords: ["ship", "hosting", "provision", "deploy", "launch", "go live", "release"] },
          { id: "broadcast", label: "Broadcast", path: `${projectBase}/broadcast`, icon: Radio, section: "Main", keywords: ["broadcast", "publish", "announce", "marketing"] },
        ]
      : []),
    { id: "objectives", label: entLabel("objective", "Objectives"), path: `${projectBase}/objectives`, icon: Target, section: "Main", keywords: ["goal", "okr", "objective"] },
    { id: "user-stories", label: entLabel("user_story", "User Stories"), path: `${projectBase}/user-stories`, icon: BookOpen, section: "Main", keywords: ["story", "user story", "epic", "persona"] },
    { id: "tasks", label: entLabel("impl_task", "Tasks"), path: `${projectBase}/tasks`, icon: CheckSquare, section: "Development", keywords: ["task", "kanban", "board", "work"] },
    { id: "components", label: entLabel("component", "Components"), path: `${projectBase}/components`, icon: Layers, section: "Development", keywords: ["component", "module", "architecture"] },
    { id: "tests", label: entLabel("test_case", "Tests"), path: `${projectBase}/tests`, icon: FlaskConical, section: "Development", keywords: ["test", "qa", "quality", "suite"] },
    { id: "defects", label: entLabel("defect", "Defects"), path: `${projectBase}/defects`, icon: Bug, section: "Development", keywords: ["bug", "defect", "issue", "error"] },
    { id: "documents", label: entLabel("document", "Documents"), path: `${projectBase}/documents`, icon: FileText, section: "Development", keywords: ["doc", "document", "file", "ai", "generate"] },
    { id: "meetings", label: "Meetings", path: `${projectBase}/meetings`, icon: Video, section: "Management", keywords: ["meeting", "schedule", "agenda", "call"] },
    { id: "sprints", label: "Sprints", path: `${projectBase}/sprints`, icon: Calendar, section: "Management", keywords: ["sprint", "iteration", "agile", "scrum"] },
    { id: "milestones", label: "Milestones", path: `${projectBase}/milestones`, icon: Milestone, section: "Management", keywords: ["milestone", "deadline", "target"] },
    { id: "traceability", label: "Traceability", path: `${projectBase}/traceability`, icon: GitBranch, section: "Management", keywords: ["trace", "matrix", "link", "coverage"] },
    { id: "baselines", label: "Baselines", path: `${projectBase}/baselines`, icon: GitCompare, section: "Management", keywords: ["baseline", "snapshot", "version"] },
    { id: "change-control", label: "Change Control", path: `${projectBase}/change-control`, icon: Shield, section: "Management", keywords: ["change", "ccb", "impact"] },
    { id: "reports", label: "Reports", path: `${projectBase}/reports`, icon: BarChart3, section: "Management", keywords: ["report", "chart"] },
    // Founder 6467901243: the ISW demo's lessons, checked before the screen is shared.
    { id: "demo-check", label: "Pre-demo check", path: `${projectBase}/demo-check`, icon: Shield, section: "Management", keywords: ["demo", "present", "presenting", "ready", "check", "rehearse"] },
    { id: "analytics", label: "Analytics", path: `${projectBase}/analytics`, icon: BarChart3, section: "Management", keywords: ["analytics", "health", "metrics", "dashboard", "kpi", "completion"] },
    { id: "profile", label: "My Profile", path: "/profile", icon: Users, section: "Admin", keywords: ["profile", "account", "avatar", "timezone"] },
    // Settings repointed to the real page (nav-consolidation PR-1): /settings
    // is only a redirect to /organization/settings in App.tsx. The Status
    // Registry entry was dropped — /status-registry is a redirect to
    // /organization/settings?domain=workflow, i.e. the same destination.
    { id: "settings", label: "Settings", path: "/organization/settings", icon: Settings, section: "Admin", keywords: ["settings", "config", "admin", "platform", "organization", "status", "workflow"] },
    { id: "roles", label: "Roles", path: "/roles", icon: Users, section: "Admin", keywords: ["role", "permission", "rbac", "access"] },
    { id: "audit-log", label: "Audit Log", path: "/audit-log", icon: ScrollText, section: "Admin", keywords: ["audit", "log", "history", "trail"] },
    // Workspace
    { id: "gmail", label: "Gmail", path: "/gmail", icon: Mail, section: "Workspace", keywords: ["email", "inbox", "gmail", "mail", "send"] },
    { id: "calendar", label: "Calendar", path: "/calendar", icon: CalendarDays, section: "Workspace", keywords: ["calendar", "schedule", "event", "meeting", "availability"] },
    { id: "contacts", label: "Contacts", path: "/contacts", icon: Contact, section: "Workspace", keywords: ["contact", "directory", "people", "phone", "address"] },
    { id: "drive", label: "Drive", path: "/drive", icon: HardDrive, section: "Workspace", keywords: ["drive", "file", "folder", "upload", "document", "google drive"] },

    // Broadcast (marketing automation). These are ORG-level routes — note there
    // is no `:projectId` in any of them, so they belong here and NOT beside the
    // project-scoped `${projectBase}/broadcast` entry above, which would build a
    // broken path. Until now none of the seven surfaces had any entry point at
    // all: nav, palette, or otherwise.
    { id: "marketing-leads", label: "Leads", path: "/marketing/leads", icon: Contact, section: "Broadcast", keywords: ["lead", "prospect", "marketing", "broadcast", "crm"] },
    { id: "marketing-segments", label: "Segments", path: "/marketing/segments", icon: Filter, section: "Broadcast", keywords: ["segment", "audience", "list", "targeting", "marketing"] },
    { id: "marketing-templates", label: "Campaign templates", path: "/marketing/templates", icon: LayoutTemplate, section: "Broadcast", keywords: ["template", "campaign", "email", "marketing", "broadcast"] },
    { id: "marketing-content", label: "Content studio", path: "/marketing/content", icon: PenLine, section: "Broadcast", keywords: ["content", "studio", "copy", "creative", "marketing"] },
    { id: "marketing-social", label: "Social composer", path: "/marketing/social", icon: Share2, section: "Broadcast", keywords: ["social", "post", "compose", "twitter", "linkedin", "marketing"] },
    { id: "marketing-approvals", label: "Approvals queue", path: "/marketing/approvals", icon: CheckSquare, section: "Broadcast", keywords: ["approval", "review", "queue", "marketing", "broadcast"] },
    { id: "compliance", label: "Compliance & data requests", path: "/compliance", icon: Shield, section: "Admin", keywords: ["compliance", "gdpr", "dsar", "privacy", "erasure", "export", "data request"] },
    { id: "marketing-analytics", label: "Campaign analytics", path: "/marketing/analytics", icon: BarChart3, section: "Broadcast", keywords: ["analytics", "campaign", "performance", "open rate", "marketing"] },
  ].filter(
    (item) =>
      (iteratorEnabled || item.id !== "iterator")
      && !isHiddenSurface(item.path, hiddenSurfaces),
  );
}

// ---- Entity search (unified API call) ----

const ALL_ENTITY_TYPES: EntityType[] = [
  "Project",
  "Objective",
  "Task",
  "Test Case",
  "Defect",
  "Document",
  "Component",
  "User Story",
  "Module",
  "Specification",
];

// Map API entity_type strings to display EntityType
const API_TYPE_TO_DISPLAY: Record<string, EntityType> = {
  project: "Project",
  objective: "Objective",
  user_story: "User Story",
  component: "Component",
  impl_task: "Task",
  test_case: "Test Case",
  module: "Module",
  component_specification: "Specification",
};

// Display EntityType → canonical entity-type slug, for the template-layer SKIN
// of the search-result type chips/badges. Types without a project-scoped
// canonical (Project, Specification) are omitted and shown verbatim.
const DISPLAY_TYPE_TO_CANONICAL: Partial<Record<EntityType, string>> = {
  Objective: "objective",
  "User Story": "user_story",
  Task: "impl_task",
  "Test Case": "test_case",
  Defect: "defect",
  Document: "document",
  Component: "component",
  Module: "module",
};

// Map display EntityType to API entity_type for filtering
const DISPLAY_TYPE_TO_API: Partial<Record<EntityType, string>> = {
  Project: "project",
  Objective: "objective",
  "User Story": "user_story",
  Component: "component",
  Task: "impl_task",
  "Test Case": "test_case",
  Module: "module",
  Specification: "component_specification",
};

// Entity types that live OUTSIDE a project. Their routes are absolute
// (App.tsx:487 declares path="/conversations"), so prefixing them with
// /projects/{id}/ produces a 404 even though the destination exists.
const GLOBAL_ENTITY_PATHS: Record<string, string> = {
  conversation_session: "conversations",
  conversation_turn: "conversations",
};

// Map API entity_type to URL path.
//
// The backend can return ELEVEN entity types (_ENTITY_TABLES in
// embedding_service.py, and search.py defaults to all of them when no `types`
// filter is sent — which is exactly what this component sends when no type chip
// is selected). This map covered SEVEN. The `?? entityType` fallback then built
// a path from the raw type name, so conversation_session, conversation_turn,
// qa_action_item and qa_agent_test each rendered as a perfectly ordinary,
// clickable search result that 404s.
//
// All four DO have real destinations — App.tsx:455 action-items, :458 qa-agent,
// :487 /conversations — so they are MAPPED here rather than filtered out of the
// query. Suppressing them would have hidden real content to hide a routing bug.
export function getEntityPath(entityType: string, projectId?: string): string {
  // A project result carries its own id as project_id: land on its home.
  if (entityType === "project") return projectId ? `/projects/${projectId}` : "/projects";
  const globalSegment = GLOBAL_ENTITY_PATHS[entityType];
  if (globalSegment) return `/${globalSegment}`;

  const pathMap: Record<string, string> = {
    objective: "objectives",
    user_story: "user-stories",
    component: "components",
    impl_task: "tasks",
    test_case: "tests",
    module: "modules",
    component_specification: "specifications",
    qa_action_item: "action-items",
    qa_agent_test: "qa-agent",
  };
  const segment = pathMap[entityType];
  // An UNKNOWN type has no honest destination. Sending it to /{raw_type} is how
  // this bug worked: it looks like a link and lands on a 404. Global search is
  // the least-bad landing place, and it keeps the result usable.
  if (!segment) return projectId ? `/projects/${projectId}` : "/search";
  return projectId ? `/projects/${projectId}/${segment}` : `/${segment}`;
}

// Chat transcript rows are never ⌘K results: the palette goes places, and a
// conversation turn ("OK, you never answered my question…") is not a place
// (founder 6427458847). search.py already drops them; this is the second lock.
const NEVER_IN_PALETTE = new Set(["conversation_turn", "conversation_session"]);

// Drop conversation rows and put matching projects first, keeping the
// server's order otherwise ("⌘K → Keystone → choose the project").
/**
 * The title a result row SHOWS. Founder 6449877134 item 1: under Presenting a task reads without its working
 * prefix ("R29 [PM] Hold the go-live readiness review" -> "Hold the go-live readiness review"), as the task pages
 * do (lib/plainTitle). Search, highlighting input and the link are unchanged; other types are shown as stored.
 */
export function shownEntityTitle(title: string, type: string, presenting: boolean): string {
  return type === API_TYPE_TO_DISPLAY.impl_task ? plainTaskTitle(title, presenting) : title;
}

export function paletteEntityItems(items: UnifiedSearchResult[]): UnifiedSearchResult[] {
  const kept = items.filter((item) => !NEVER_IN_PALETTE.has(item.entity_type));
  return [
    ...kept.filter((item) => item.entity_type === "project"),
    ...kept.filter((item) => item.entity_type !== "project"),
  ];
}

function useEntitySearch(
  query: string,
  projectId?: string,
  typeFilter?: EntityType | null,
) {
  const [results, setResults] = useState<EntityResult[]>([]);
  const [embeddingUsed, setEmbeddingUsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      setEmbeddingUsed(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        // Build types filter for the API
        const typesParam = typeFilter
          ? DISPLAY_TYPE_TO_API[typeFilter]
          : undefined;

        const response = await searchApi.search({
          q: query,
          types: typesParam,
          project_id: projectId,
          limit: 20,
        });

        setEmbeddingUsed(response.embedding_used);

        const mapped: EntityResult[] = paletteEntityItems(response.items).map((item) => ({
          id: item.id,
          title: item.title || "Untitled",
          type: API_TYPE_TO_DISPLAY[item.entity_type] ?? item.entity_type,
          // ITEM'S OWN PROJECT, not the ambient one. Search is org-wide when no
          // project filter is set (search.py:73 selects project_id per row and
          // :90 returns it), so routing every hit through the project the user
          // happens to be standing in sent cross-project results to a URL where
          // that entity does not exist — a 404 that looks like a missing record
          // rather than a bad link. Falls back to the ambient project so
          // in-project search is unchanged.
          path: getEntityPath(item.entity_type, item.project_id || projectId),
          status: item.status || undefined,
          similarity: item.similarity,
          matchSource: item.match_source,
        }));

        setResults(mapped.slice(0, 20));
      } catch {
        setResults([]);
        setEmbeddingUsed(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timerRef.current);
  }, [query, projectId, typeFilter]);

  return { results, loading, embeddingUsed };
}

/** Search workspace sources (Gmail, Calendar, Drive) in parallel */
function useWorkspaceSearch(query: string) {
  const [results, setResults] = useState<EntityResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const [gmailRes, calRes, driveRes] = await Promise.allSettled([
          gmailApi.listMessages({ q: query, max_results: 5 }),
          calendarApi.listEvents({ q: query, max_results: 5 }),
          driveApi.searchFiles(query, 5),
        ]);

        const mapped: EntityResult[] = [];

        // Gmail results
        if (gmailRes.status === "fulfilled") {
          const msgs = (gmailRes.value as Record<string, unknown>)?.messages as Array<Record<string, unknown>> || [];
          for (const m of msgs.slice(0, 3)) {
            mapped.push({
              id: m.gmail_message_id as string,
              title: (m.subject as string) || "(no subject)",
              type: "Email",
              path: "/gmail",
              subtitle: m.from_name as string || m.from_address as string || "",
            });
          }
        }

        // Calendar results
        if (calRes.status === "fulfilled") {
          const events = (calRes.value as Record<string, unknown>)?.events as Array<Record<string, unknown>> || [];
          for (const e of events.slice(0, 3)) {
            mapped.push({
              id: e.google_event_id as string,
              title: (e.summary as string) || "Untitled Event",
              type: "Event",
              path: "/calendar",
              subtitle: e.start_time ? new Date(e.start_time as string).toLocaleDateString() : "",
            });
          }
        }

        // Drive results
        if (driveRes.status === "fulfilled") {
          const files = (driveRes.value as Record<string, unknown>)?.items as Array<Record<string, unknown>> || [];
          for (const f of files.slice(0, 3)) {
            mapped.push({
              id: f.google_file_id as string,
              title: (f.name as string) || "Untitled File",
              type: "File",
              path: "/drive",
              subtitle: f.last_modifying_user as string || "",
            });
          }
        }

        setResults(mapped);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timerRef.current);
  }, [query]);

  return { results, loading };
}

// Workspace type icons
const WORKSPACE_TYPE_ICONS: Record<string, { icon: LucideIcon; color: string }> = {
  Email: { icon: Mail, color: "text-rose-600 dark:text-rose-400" },
  Event: { icon: CalendarDays, color: "text-violet-600 dark:text-violet-400" },
  File: { icon: HardDrive, color: "text-amber-600 dark:text-amber-400" },
};

// ---- Component ----

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [typeFilter, setTypeFilter] = useState<EntityType | null>(null);
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const closePalette = useCallback(() => {
    setOpen(false);
    setQuery("");
    setTypeFilter(null);
  }, []);
  // Focus-trap + focus-restore + scroll-lock. Keeps the search input focused
  // on open via initialFocusRef. Escape is handled by the component's own
  // key handler, so disable the hook's to avoid double-close.
  const dialogRef = useModalA11y<HTMLDivElement>({
    open,
    onClose: closePalette,
    dismissable: false,
    initialFocusRef: inputRef,
  });
  const navigate = useNavigate();
  // A result keeps the project mode the reader is in, so the new rail stays
  // on screen (founder 6428293584 (B)).
  const { search: locationSearch } = useLocation();
  const { projectId } = useParams<{ projectId: string }>();
  // Template-layer SKIN for the project-scoped entity nav rows + result
  // type chips/badges. `skinDisplayType` relabels a display EntityType to the
  // project's manifest vocabulary; the display word is the zero-drift fallback,
  // so search results outside a project (or on a non-template project) are
  // byte-identical.
  const { label: skinLabel, hiddenSurfaces } = useTemplateLabels(projectId ?? null);
  const skinDisplayType = (type: EntityType, plural = false): string => {
    const canonical = DISPLAY_TYPE_TO_CANONICAL[type];
    const fallback = plural ? `${type}s` : type;
    return canonical ? skinLabel(canonical, { plural, fallback }) : fallback;
  };

  // Fails OPEN while in flight, matching FelixRail: hiding a command on a
  // loading state makes the palette's contents flicker, and the page refuses
  // anyway.
  const iteratorFlag = useV3Flag(V3_FLAGS.ITERATOR_MVP);
  const iteratorEnabled = iteratorFlag.isLoading || iteratorFlag.enabled;

  const items = useMemo(
    () => buildItems(
      projectId ? `/projects/${projectId}` : "", skinLabel, iteratorEnabled, hiddenSurfaces,
    ),
    [projectId, skinLabel, iteratorEnabled, hiddenSurfaces],
  );

  const { results: rawEntityResults, loading: entityLoading, embeddingUsed } =
    useEntitySearch(open ? query : "", projectId, typeFilter);
  // Presenting: the demo project's copies never show (founder 6441608339 item 5).
  const presenting = usePresenting((st) => st.presenting);
  const entityResults = useMemo(
    () => (presenting ? withoutProjectCopies(rawEntityResults) : rawEntityResults),
    [presenting, rawEntityResults],
  );

  const { results: workspaceResults, loading: workspaceLoading } =
    useWorkspaceSearch(open ? query : "");

  const recentItems = useMemo(
    () => (open && !query.trim() ? getRecentItems() : []),
    [open, query],
  );

  // Load search history when opened
  useEffect(() => {
    if (open) {
      setSearchHistory(getSearchHistory());
    }
  }, [open]);

  // Filter navigation items by query
  const filtered = query.trim()
    ? items.filter((item) => {
        const q = query.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) ||
          item.section.toLowerCase().includes(q) ||
          item.keywords.some((kw) => kw.includes(q))
        );
      })
    : items;

  // Group filtered items by section
  const sections = filtered.reduce<Record<string, PaletteItem[]>>(
    (acc, item) => {
      (acc[item.section] ??= []).push(item);
      return acc;
    },
    {},
  );
  // "Workspace" was defined in buildItems but missing here, so its four
  // entries (Gmail/Calendar/Contacts/Drive) never rendered — both the visible
  // list and the keyboard-selection flat list iterate this array
  // (nav-consolidation PR-1 correctness fix).
  const baseSectionOrder = ["Main", "Development", "Management", "Admin", "Workspace"];
  // An exact label match ranks first: its section leads and it leads its
  // section. Typing "Meetings" + Enter opened /calendar because
  // Communications (keyword "meetings") sits in the earlier Main section
  // (10-26 page audit).
  const exactQuery = query.trim().toLowerCase();
  const exactMatch = exactQuery
    ? filtered.find((item) => item.label.toLowerCase() === exactQuery)
    : undefined;
  if (exactMatch) {
    sections[exactMatch.section] = [
      exactMatch,
      ...(sections[exactMatch.section] ?? []).filter((i) => i !== exactMatch),
    ];
  }
  const sectionOrder = exactMatch
    ? [exactMatch.section, ...baseSectionOrder.filter((s) => s !== exactMatch.section)]
    : baseSectionOrder;
  const flatFiltered = sectionOrder.flatMap((s) => sections[s] ?? []);

  // Collect type counts for filter chips
  const typeCounts = useMemo(() => {
    const counts: Partial<Record<EntityType, number>> = {};
    for (const r of entityResults) {
      const t = r.type as EntityType;
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }, [entityResults]);

  const availableTypes = useMemo(
    () => ALL_ENTITY_TYPES.filter((t) => (typeCounts[t] ?? 0) > 0),
    [typeCounts],
  );

  // Show search history when query is empty
  const showSearchHistory =
    !query.trim() && searchHistory.length > 0;

  // Total selectable items = recent + search history + navigation + entity results
  const totalItems =
    recentItems.length +
    (showSearchHistory ? searchHistory.length : 0) +
    flatFiltered.length +
    entityResults.length;

  const handleSelect = useCallback(
    (item: PaletteItem) => {
      saveRecentItem(item);
      setOpen(false);
      setQuery("");
      setTypeFilter(null);
      navigate(preserveMode(item.path, locationSearch));
    },
    [navigate, locationSearch],
  );

  const handleEntitySelect = useCallback(
    (entity: EntityResult) => {
      if (query.trim()) saveSearchQuery(query);
      setOpen(false);
      setQuery("");
      setTypeFilter(null);
      navigate(preserveMode(entity.path, locationSearch));
    },
    [navigate, query, locationSearch],
  );

  const handleSearchHistorySelect = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      setSelectedIndex(0);
    },
    [],
  );

  // Open/close with Cmd+K
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
        setQuery("");
        setSelectedIndex(0);
        setTypeFilter(null);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
        setQuery("");
        setTypeFilter(null);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Arrow navigation and Enter selection
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        let idx = selectedIndex;
        // Recent items
        if (recentItems.length > 0 && idx < recentItems.length) {
          const item = items.find((i) => i.id === recentItems[idx].id);
          if (item) handleSelect(item);
          return;
        }
        idx -= recentItems.length;
        // Search history items
        if (showSearchHistory && idx < searchHistory.length) {
          handleSearchHistorySelect(searchHistory[idx]);
          return;
        }
        if (showSearchHistory) idx -= searchHistory.length;
        // Navigation items
        if (idx < flatFiltered.length) {
          handleSelect(flatFiltered[idx]);
          return;
        }
        idx -= flatFiltered.length;
        // Entity results
        if (idx < entityResults.length) {
          handleEntitySelect(entityResults[idx]);
        }
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    selectedIndex,
    totalItems,
    recentItems,
    flatFiltered,
    entityResults,
    items,
    handleSelect,
    handleEntitySelect,
    showSearchHistory,
    searchHistory,
    handleSearchHistorySelect,
  ]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Reset index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  if (!open) return null;

  let flatIndex = -1;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={() => {
          setOpen(false);
          setQuery("");
          setTypeFilter(null);
        }}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        {...dialogA11yProps({ label: "Command palette" })}
        className="fixed left-1/2 top-[20%] z-50 w-full max-w-lg -translate-x-1/2 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl animate-fade-in"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
          <Search className="h-5 w-5 text-slate-500 dark:text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, entities, actions..."
            className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:text-slate-400 outline-none"
          />
          {entityLoading && (
            <Loader2 className="h-4 w-4 text-slate-500 dark:text-slate-400 animate-spin" />
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 dark:text-slate-400">
            ESC
          </kbd>
        </div>

        {/* Type filter chips — shown when entity results exist */}
        {query.trim().length >= 3 && availableTypes.length > 1 && (
          <div className="flex items-center gap-1.5 border-b border-slate-200 dark:border-slate-700 px-4 py-2 overflow-x-auto">
            <button
              onClick={() => setTypeFilter(null)}
              className={cn(
                "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                !typeFilter
                  ? "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80",
              )}
            >
              All ({entityResults.length})
            </button>
            {availableTypes.map((type) => {
              const meta = ENTITY_TYPE_META[type];
              const Icon = meta.icon;
              return (
                <button
                  key={type}
                  onClick={() =>
                    setTypeFilter(typeFilter === type ? null : type)
                  }
                  className={cn(
                    "shrink-0 flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                    typeFilter === type
                      ? "bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80",
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {skinDisplayType(type, true)} ({typeCounts[type]})
                </button>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto py-2">
          {/* Recent items (when no query) */}
          {recentItems.length > 0 && !query.trim() && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Recent
              </div>
              {recentItems.map((recent) => {
                flatIndex++;
                const idx = flatIndex;
                const isSelected = idx === selectedIndex;
                const fullItem = items.find((i) => i.id === recent.id);
                const Icon = fullItem?.icon ?? ArrowRight;
                return (
                  <button
                    key={`recent-${recent.id}`}
                    data-index={idx}
                    onClick={() => {
                      if (fullItem) handleSelect(fullItem);
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                      isSelected
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                        : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                  >
                    <Icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isSelected
                          ? "text-brand-600 dark:text-brand-400"
                          : "text-slate-500 dark:text-slate-400",
                      )}
                    />
                    <span className="flex-1 text-left font-medium">
                      {recent.label}
                    </span>
                    {isSelected && (
                      <ArrowRight className="h-3.5 w-3.5 text-brand-400" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Search history (when no query) */}
          {showSearchHistory && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <History className="h-3 w-3" />
                  Recent Searches
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSearchHistory();
                    setSearchHistory([]);
                  }}
                  className="text-[10px] text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  Clear
                </button>
              </div>
              {searchHistory.map((searchQuery) => {
                flatIndex++;
                const idx = flatIndex;
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={`search-${searchQuery}`}
                    data-index={idx}
                    onClick={() => handleSearchHistorySelect(searchQuery)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors",
                      isSelected
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                        : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                  >
                    <Search
                      className={cn(
                        "h-3.5 w-3.5 shrink-0",
                        isSelected
                          ? "text-brand-600 dark:text-brand-400"
                          : "text-slate-500 dark:text-slate-400",
                      )}
                    />
                    <span className="flex-1 text-left truncate">
                      {searchQuery}
                    </span>
                    {isSelected && (
                      <ArrowRight className="h-3.5 w-3.5 text-brand-400" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Navigation items */}
          {flatFiltered.length === 0 &&
          entityResults.length === 0 &&
          !showSearchHistory ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              No results found for &ldquo;{query}&rdquo;
            </div>
          ) : (
            sectionOrder.map((sectionName) => {
              const sectionItems = sections[sectionName];
              if (!sectionItems?.length) return null;
              return (
                <div key={sectionName}>
                  <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {sectionName}
                  </div>
                  {sectionItems.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const isSelected = idx === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        data-index={idx}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={cn(
                          "flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                          isSelected
                            ? "bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                            : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                        )}
                      >
                        <item.icon
                          className={cn(
                            "h-4 w-4 shrink-0",
                            isSelected
                              ? "text-brand-600 dark:text-brand-400"
                              : "text-slate-500 dark:text-slate-400",
                          )}
                        />
                        <span className="flex-1 text-left font-medium">
                          <HighlightMatch
                            text={item.label}
                            query={query}
                          />
                        </span>
                        {isSelected && (
                          <ArrowRight className="h-3.5 w-3.5 text-brand-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}

          {/* Entity search results */}
          {entityResults.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Search className="h-3 w-3" />
                {/* Plain English on the tour (founder 6433982274): "Matches", and no
                  * "AI" label; a meaning match keeps a small mark with a plain title. */}
                Matches
                {embeddingUsed && (
                  <span title="Matched by meaning" aria-label="Matched by meaning"
                    className="ml-1 inline-flex items-center text-purple-700 dark:text-purple-300 normal-case tracking-normal">
                    <Sparkles className="h-2.5 w-2.5" />
                  </span>
                )}
              </div>
              {entityResults.map((entity) => {
                flatIndex++;
                const idx = flatIndex;
                const isSelected = idx === selectedIndex;
                const meta =
                  ENTITY_TYPE_META[entity.type as EntityType];
                const TypeIcon = meta?.icon ?? Search;
                const typeColor = meta?.color ?? "text-slate-500 dark:text-slate-400";
                return (
                  <button
                    key={`entity-${entity.type}-${entity.id}`}
                    data-index={idx}
                    onClick={() => handleEntitySelect(entity)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                      isSelected
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                        : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                  >
                    <TypeIcon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isSelected
                          ? "text-brand-600 dark:text-brand-400"
                          : typeColor,
                      )}
                    />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="truncate font-medium">
                        <HighlightMatch
                          text={shownEntityTitle(entity.title, entity.type, presenting)}
                          query={query}
                        />
                      </div>
                      {entity.subtitle && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {entity.subtitle}
                        </div>
                      )}
                    </div>
                    <span
                      className={cn(
                        "shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded",
                        isSelected
                          ? "bg-brand-100 text-brand-700 dark:bg-brand-800 dark:text-brand-300"
                          : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400",
                      )}
                    >
                      {skinDisplayType(entity.type as EntityType)}
                    </span>
                    {entity.status && (
                      <span
                        className={cn(
                          "shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded",
                          getStatusColor(entity.status),
                        )}
                      >
                        {plainStatus(entity.status)}
                      </span>
                    )}
                    {isSelected && (
                      <ArrowRight className="h-3.5 w-3.5 text-brand-400" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Workspace search results (Gmail, Calendar, Drive) */}
          {workspaceResults.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <Mail className="h-3 w-3" />
                Workspace
              </div>
              {workspaceResults.map((item) => {
                flatIndex++;
                const idx = flatIndex;
                const isSelected = idx === selectedIndex;
                const wmeta = WORKSPACE_TYPE_ICONS[item.type];
                const WIcon = wmeta?.icon ?? Search;
                const wColor = wmeta?.color ?? "text-slate-500 dark:text-slate-400";
                return (
                  <button
                    key={`ws-${item.type}-${item.id}`}
                    data-index={idx}
                    onClick={() => {
                      navigate(item.path);
                      setOpen(false);
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                      isSelected
                        ? "bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-300"
                        : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                  >
                    <WIcon className={cn("h-4 w-4 shrink-0", isSelected ? "text-brand-600 dark:text-brand-400" : wColor)} />
                    <div className="flex-1 min-w-0 text-left">
                      <div className="truncate font-medium">
                        <HighlightMatch text={item.title} query={query} />
                      </div>
                      {item.subtitle && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{item.subtitle}</div>
                      )}
                    </div>
                    <span className={cn(
                      "shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded",
                      isSelected ? "bg-brand-100 text-brand-700 dark:bg-brand-800 dark:text-brand-300" : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400",
                    )}>
                      {item.type}
                    </span>
                    {isSelected && <ArrowRight className="h-3.5 w-3.5 text-brand-400" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Loading state for entity search */}
          {(entityLoading || workspaceLoading) && entityResults.length === 0 && workspaceResults.length === 0 && query.length >= 3 && (
            <div className="px-4 py-4 text-center text-sm text-slate-500 dark:text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching entities...
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-2">
          <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 py-0.5 font-mono">
                ↑↓
              </kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 py-0.5 font-mono">
                ↵
              </kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1 py-0.5 font-mono">
                esc
              </kbd>
              close
            </span>
          </div>
          <div className="flex items-center gap-1 text-[10px] text-slate-500 dark:text-slate-400">
            <Command className="h-3 w-3" />
            <span>K to toggle</span>
          </div>
        </div>
      </div>
    </>
  );
}
